'use client';

import { forwardRef, type InputHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

export const inputVariants = cva(
    'box-border w-full resize-none rounded-lg border border-solid bg-transparent px-3 py-[13px] text-2xs font-medium leading-5 text-typo-primary placeholder:text-typo-secondary group-focus-within:outline-none',
    {
        variants: {
            isError: {
                true: 'border-red-800 group-focus-within:border-red-800 group-hover:border-red-800',
                false: 'border-grey-200 group-focus-within:border-green-600 group-hover:border-grey-400 group-focus-within:group-hover:border-green-600',
            },
        },
        defaultVariants: {
            isError: false,
        },
    },
);

export const labelVariants = cva(
    'absolute left-2 cursor-text bg-background px-2 text-typo-secondary transition-all',
    {
        variants: {
            isError: {
                true: 'group-focus-within:text-red-800 group-focus-within:group-hover:text-red-800',
                false: 'group-focus-within:text-green-600 group-focus-within:group-hover:text-green-600',
            },
            isValue: {
                true: 'top-[-7px] z-[2] text-3xs',
                false: 'top-[14px] text-2xs group-focus-within:top-[-7px] group-focus-within:z-[2] group-focus-within:text-3xs',
            },
        },
        defaultVariants: {
            isError: false,
            isValue: false,
        },
    },
);

export interface InputProps
    extends InputHTMLAttributes<HTMLInputElement>,
        VariantProps<typeof inputVariants> {
    isError?: boolean;
    label?: string;
    handleClearValue?: () => void;
    'data-test-id'?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ isError, label, id, name, value, className, ...props }, ref) => (
        <div className="group relative">
            {!!label && (
                <label
                    htmlFor={id || name}
                    className={labelVariants({
                        isError,
                        isValue: !!value,
                    })}
                >
                    {label}
                </label>
            )}

            <input
                id={id || name}
                name={name || ''}
                className={cn(inputVariants({ isError, className }))}
                value={value}
                ref={ref}
                aria-invalid={!!isError}
                {...props}
            />
        </div>
    ),
);

export default Input;
